import { useState } from "react";
import type { FormEvent, ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { startRegistration } from "@simplewebauthn/browser";
import { Fingerprint, LockOpen, Plus, Trash2 } from "lucide-react";
import { deletePasskey, disablePasskeyUnlock, passkeyRegistrationOptions, verifyPasskeyRegistration } from "../../api/security.js";
import type { PasskeyMeta } from "../../api/security.js";
import { ApiClientError } from "../../api/http.js";
import { describeAuthError, isPasskeyCancel } from "../../app/authFlow.js";
import { passkeyUnlockAvailable, turnOnPasskeyUnlock } from "../../app/passkeyUnlock.js";
import { isDesktop } from "../../app/platform.js";
import { useSession } from "../../app/session.js";
import { ME_KEY } from "../../data/account.js";
import { PASSKEYS_KEY, TWO_FACTOR_KEY, usePasskeys } from "../../data/security.js";
import { formatDateTime, formatRelativeTime } from "../../lib/format.js";
import { Dialog } from "../../ui/Dialog.js";
import { Checkbox, Field, TextInput } from "../../ui/forms.js";
import { Alert, Badge, Button, IconButton, Section, Skeleton } from "../../ui/primitives.js";
import { useToast } from "../../ui/Toast.js";

/** Settings: passkeys as second factor and, where the device supports PRF, for unlocking without the master password. */
export function PasskeysSection(): ReactNode {
  const { state } = useSession();
  const queryClient = useQueryClient();
  const toast = useToast();
  const passkeys = usePasskeys();
  const [open, setOpen] = useState(false);
  const [nickname, setNickname] = useState("");
  const [withUnlock, setWithUnlock] = useState(true);
  const [busy, setBusy] = useState<string | undefined>(undefined);
  const [error, setError] = useState<string | undefined>(undefined);
  const desktop = isDesktop();
  const supported = passkeyUnlockAvailable();

  async function refresh(): Promise<void> {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: TWO_FACTOR_KEY }),
      queryClient.invalidateQueries({ queryKey: PASSKEYS_KEY }),
      queryClient.invalidateQueries({ queryKey: ME_KEY })
    ]);
  }

  async function add(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (busy !== undefined) {
      return;
    }
    const name = nickname.trim() === "" ? "Mein Gerät" : nickname.trim();
    setBusy("add");
    setError(undefined);
    let added: string | undefined;
    try {
      const options = await passkeyRegistrationOptions();
      const response = await startRegistration({ optionsJSON: options });
      added = (await verifyPasskeyRegistration(name, response)).id;
    } catch (err: unknown) {
      setError(isPasskeyCancel(err) ? "Die Bestätigung wurde abgebrochen. Der Passkey muss mit PIN oder Biometrie bestätigt werden." : "Passkey konnte nicht hinzugefügt werden.");
      setBusy(undefined);
      return;
    }
    setOpen(false);
    setNickname("");
    let unlockProblem: string | undefined;
    if (withUnlock && state.status === "unlocked") {
      try {
        await turnOnPasskeyUnlock(state.client, added);
      } catch (err: unknown) {
        unlockProblem = isPasskeyCancel(err) ? "Entsperren nicht eingeschaltet: Die Bestätigung wurde abgebrochen." : describeAuthError(err);
      }
    }
    await refresh();
    setBusy(undefined);
    if (unlockProblem !== undefined) {
      toast.notify(`Passkey hinzugefügt. ${unlockProblem}`, { tone: "danger" });
    } else {
      toast.notify(withUnlock ? "Passkey hinzugefügt. Er entsperrt jetzt auch den Tresor." : "Passkey hinzugefügt.", { tone: "success" });
    }
  }

  async function setUnlock(key: PasskeyMeta, on: boolean): Promise<void> {
    if (busy !== undefined || state.status !== "unlocked") {
      return;
    }
    setBusy(key.id);
    try {
      if (on) {
        await turnOnPasskeyUnlock(state.client, key.id);
      } else {
        await disablePasskeyUnlock(key.id);
      }
      await refresh();
      toast.notify(on ? `${key.nickname} entsperrt jetzt den Tresor.` : `${key.nickname} entsperrt den Tresor nicht mehr.`, { tone: "success" });
    } catch (err: unknown) {
      toast.notify(isPasskeyCancel(err) ? "Die Bestätigung wurde abgebrochen." : describeAuthError(err), { tone: "danger" });
    } finally {
      setBusy(undefined);
    }
  }

  async function remove(id: string): Promise<void> {
    if (busy !== undefined) {
      return;
    }
    setBusy(id);
    try {
      await deletePasskey(id);
      await refresh();
      toast.notify("Passkey entfernt.");
    } catch (err: unknown) {
      toast.notify(err instanceof ApiClientError && err.status === 409 ? err.message : "Entfernen fehlgeschlagen.", { tone: "danger" });
    } finally {
      setBusy(undefined);
    }
  }

  return (
    <>
      <Section
        title="Passkeys"
        description="Anmelden mit Gerät, Sicherheitsschlüssel oder Passkey-Manager – als zweiter Faktor oder, wo das Gerät es kann, zum Entsperren ohne Master-Passwort."
        aside={
          supported ? (
            <Button size="sm" variant="secondary" icon={Plus} onClick={(): void => { setError(undefined); setOpen(true); }}>
              Passkey hinzufügen
            </Button>
          ) : undefined
        }
      >
        <div className="flex flex-col gap-3">
          {!supported ? (
            <Alert tone="info">
              {desktop ? "Passkeys lassen sich nur in der Web-App einrichten und nutzen. Vorhandene Passkeys kannst du hier entfernen." : "Dieser Browser unterstützt keine Passkeys."}
            </Alert>
          ) : null}
          {passkeys.isPending ? <Skeleton className="h-16" /> : null}
          {passkeys.data !== undefined && passkeys.data.length === 0 ? (
            <p className="rounded-lg border border-dashed border-line-strong px-5 py-6 text-center text-sm text-mist">Noch kein Passkey eingerichtet.</p>
          ) : null}
          <ul className="divide-y divide-line overflow-hidden rounded-lg border border-line bg-ink-2 empty:hidden">
            {(passkeys.data ?? []).map((key: PasskeyMeta) => (
              <li key={key.id} className="flex flex-wrap items-center gap-3 px-4 py-3">
                <span className="grid size-8 shrink-0 place-items-center rounded-lg border border-line bg-ink-3 text-flow">
                  <Fingerprint size={15} aria-hidden="true" />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="flex items-center gap-2 text-sm font-medium text-foam">
                    <span className="truncate">{key.nickname}</span>
                    {key.unlockEnabledAt !== null ? <Badge tone="mint" icon={LockOpen}>Entsperrt den Tresor</Badge> : null}
                  </p>
                  <p className="text-[0.8125rem] text-mist-2" title={formatDateTime(key.createdAt)}>
                    Hinzugefügt {formatRelativeTime(key.createdAt, Date.now())}
                  </p>
                </div>
                {key.unlockEnabledAt !== null ? (
                  <Button size="sm" variant="ghost" loading={busy === key.id} disabled={busy !== undefined && busy !== key.id} onClick={(): void => void setUnlock(key, false)}>
                    Entsperren aus
                  </Button>
                ) : supported ? (
                  <Button size="sm" variant="secondary" icon={LockOpen} loading={busy === key.id} disabled={busy !== undefined && busy !== key.id} onClick={(): void => void setUnlock(key, true)}>
                    Zum Entsperren nutzen
                  </Button>
                ) : null}
                <IconButton label={`Passkey ${key.nickname} entfernen`} icon={Trash2} tone="danger" size="sm" disabled={busy !== undefined} onClick={(): void => void remove(key.id)} />
              </li>
            ))}
          </ul>
          {supported ? (
            <p className="text-[0.8125rem] leading-relaxed text-mist-2">
              Ein Passkey mit Entsperren öffnet deinen Tresor ohne E-Mail und Master-Passwort. Er wird dabei immer per PIN oder Biometrie bestätigt. Das Master-Passwort bleibt gültig und wird für Passwortwechsel, Notfall-Kit und Export gebraucht.
            </p>
          ) : null}
        </div>
      </Section>

      <Dialog open={open} onClose={(): void => setOpen(false)} title="Passkey hinzufügen" description="Gib dem Gerät einen Namen, dann bestätige mit deinem Authenticator." variant="center" size="sm">
        <form onSubmit={(event): void => void add(event)} className="flex flex-col gap-5 p-5">
          <Field id="passkey-name" label="Name">
            <TextInput id="passkey-name" value={nickname} maxLength={64} placeholder="z. B. MacBook Touch ID" onChange={(event): void => setNickname(event.target.value)} autoFocus />
          </Field>
          <Checkbox id="passkey-unlock" checked={withUnlock} onChange={setWithUnlock}>
            Auch zum Entsperren nutzen. Dafür bestätigst du ein zweites Mal.
          </Checkbox>
          {error !== undefined ? <Alert tone="danger">{error}</Alert> : null}
          <Button type="submit" loading={busy === "add"} icon={Fingerprint}>
            Mit Gerät bestätigen
          </Button>
        </form>
      </Dialog>
    </>
  );
}
